import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Save,
    Loader2,
    Calendar,
    Hash,
    Briefcase,
    Users,
    CheckCircle2,
    MessageSquare
} from 'lucide-react';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import PaginatedSelect from '../../components/Common/Select/PaginatedSelect';
import CustomDatePicker from '../../components/Common/DatePicker/CustomDatePicker';
import styles from './TaskForm.module.css';

const CreateTask = () => {
    const navigate = useNavigate();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(false);



    // Form State
    const [formData, setFormData] = useState({
        project_id: '',
        title: '',
        description: '',
        priority: { value: 'medium', label: 'Medium' },
        status: { value: 'open', label: 'Open' },
        due_date: '',
        employee_ids: []
    });

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        // No initial data needed for paginated selects
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title || !formData.project_id) {
            toast.error('Please fill in required fields (Title and Project)');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                ...formData,
                project_id: formData.project_id,
                priority: formData.priority.value,
                status: formData.status.value,
                employee_ids: formData.employee_ids
            };

            const response = await api.post('/tasks', payload);
            if (response.data.success) {
                toast.success('Task created successfully');
                navigate('/tasks');
            }
        } catch (error) {
            console.error('Error creating task:', error);
            toast.error(error.response?.data?.message || 'Failed to create task');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles['task-form-container']}>
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
            {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

            <div className={styles['content-wrapper']}>
                <Header onMenuClick={() => setIsSidebarOpen(true)} />

                <main className={styles['main-content']}>
                    <Breadcrumbs />

                    <div className={styles['page-header']}>
                        <div className={styles['header-title']}>
                            <h1>Create New Task</h1>
                            <p>Assign specific work items to projects and team members.</p>
                        </div>
                    </div>

                    <div className={styles['form-card']}>
                        <form onSubmit={handleSubmit} className={styles['form-grid']}>
                            <div className={`${styles['form-group']} ${styles['full-width']}`}>
                                <label><Briefcase size={16} /> Associated Project <span className={styles['required']}>*</span></label>
                                <PaginatedSelect
                                    endpoint="/projects-dropdown"
                                    valueKey="id"
                                    labelKey="project_name"
                                    value={formData.project_id}
                                    onChange={(val) => setFormData(prev => ({ ...prev, project_id: val }))}
                                    placeholder="Select a project..."
                                    formatOptionLabel={(opt) => `${opt.label} (${opt.original?.project_code || 'PRJ'})`}
                                />
                            </div>

                            <div className={`${styles['form-group']} ${styles['full-width']}`}>
                                <label><Hash size={16} /> Task Title <span className={styles['required']}>*</span></label>
                                <input
                                    type="text"
                                    name="title"
                                    className={styles['input-field']}
                                    placeholder="e.g., Finalize structural diagrams"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className={`${styles['form-group']} ${styles['full-width']}`}>
                                <label><MessageSquare size={16} /> Description</label>
                                <textarea
                                    name="description"
                                    className={styles['textarea-field']}
                                    placeholder="Detailed instructions for the task..."
                                    value={formData.description}
                                    onChange={handleInputChange}
                                ></textarea>
                            </div>

                            <div className={styles['form-group']}>
                                <label><Calendar size={16} /> Priority</label>
                                <CustomSelect
                                    options={[
                                        { value: 'high', label: 'High' },
                                        { value: 'medium', label: 'Medium' },
                                        { value: 'low', label: 'Low' }
                                    ]}
                                    value={formData.priority}
                                    onChange={(val) => setFormData(prev => ({ ...prev, priority: val }))}
                                />
                            </div>

                            <div className={styles['form-group']}>
                                <label><CheckCircle2 size={16} /> Due Date</label>
                                <CustomDatePicker
                                    selected={formData.due_date}
                                    onChange={(date) => setFormData(prev => ({ ...prev, due_date: date ? new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0] : '' }))}
                                    placeholderText="Select deadline"
                                    minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                                />
                            </div>

                            <div className={`${styles['form-group']} ${styles['full-width']}`}>
                                <label><Users size={16} /> Assign To (Optional)</label>
                                <PaginatedSelect
                                    endpoint="/employees-dropdown"
                                    valueKey="id"
                                    labelKey="employee_name"
                                    isMulti
                                    value={formData.employee_ids}
                                    onChange={(val) => setFormData(prev => ({ ...prev, employee_ids: val || [] }))}
                                    placeholder="Select team members..."
                                />
                            </div>

                            <div className={styles['form-footer']}>
                                <button type="button" className={styles['btn-cancel']} onClick={() => navigate('/tasks')}>
                                    Cancel
                                </button>
                                <button type="submit" className={styles['btn-submit']} disabled={loading}>
                                    {loading ? <Loader2 className={styles['spinner']} /> : <Save size={18} />}
                                    <span>Create Task</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default CreateTask;
